import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import { Modal } from "../../components/ui/Modal";
import type { PublicRole } from "../../types";

interface Props {
  onClose: () => void;
}

/**
 * Formular "Zugang anfragen" auf der Login-Seite - bewusst OHNE
 * Authentifizierung nutzbar (Endpunkte unter /api/public/*), da die
 * antragstellende Person noch kein Benutzerkonto besitzt. Die Rollenliste
 * wird live aus der bestehenden Rollenverwaltung geladen, damit neue/
 * gelöschte Rollen automatisch im Dropdown erscheinen bzw. verschwinden.
 */
export function AccessRequestModal({ onClose }: Props) {
  const [roles, setRoles] = useState<PublicRole[]>([]);
  const [fullName, setFullName] = useState("");
  const [roleId, setRoleId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    api
      .get<PublicRole[]>("/api/public/roles")
      .then((r) => {
        setRoles(r);
        setRoleId((prev) => prev || String(r[0]?.id ?? ""));
      })
      .catch(() => {});
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post("/api/public/access-requests", { fullName, requestedRoleId: Number(roleId) });
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Anfrage konnte nicht übermittelt werden.");
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <Modal title="Zugang anfragen" onClose={onClose} footer={<button className="btn-primary" onClick={onClose}>Schließen</button>}>
        <div className="text-center py-4 space-y-2">
          <p className="text-3xl">✅</p>
          <p className="text-gray-200 font-medium">Anfrage erfolgreich übermittelt.</p>
          <p className="text-sm text-gray-500">
            Ein Vorstandsmitglied prüft Ihre Anfrage. Sie erhalten anschließend Ihre Zugangsdaten.
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title="Zugang anfragen"
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Abbrechen</button>
          <button className="btn-primary" form="access-request-form" disabled={submitting}>
            {submitting ? "Wird gesendet…" : "Anfrage absenden"}
          </button>
        </>
      }
    >
      <form id="access-request-form" onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}

        <p className="text-sm text-gray-500">
          Beantragen Sie hier einen Zugang zum LBV-System. Ein Vorstandsmitglied prüft Ihre Anfrage.
        </p>

        <div>
          <label className="label">Vollständiger Name *</label>
          <input
            className="input"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Vor- und Nachname"
            autoFocus
            required
          />
        </div>

        <div>
          <label className="label">Gewünschte Rolle *</label>
          <select className="input" value={roleId} onChange={(e) => setRoleId(e.target.value)} required>
            {roles.length === 0 && <option value="">Wird geladen…</option>}
            {roles.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        </div>
      </form>
    </Modal>
  );
}
